/* eslint-disable @typescript-eslint/default-param-last */
import { IPlatformRole, IPlatformProduct, PlatformRoleType } from '@/services/users/types';
import { LICENSE_TYPE, PLATFORM_ROLE_TYPE } from '@/consts/common';
import { ILicenseType } from '@/services/licenses/types';
import { getI18nMessage } from '@/utils/helpers/i18nHelper';
import { getCurrentOrganizationUuid, getCurrentUuid } from '@/utils/helpers/authHelpers';

const HOLOXR_PLATFORM_PRODUCT: IPlatformProduct = 'holoxr';

type PlatformRolePatchAction = 'attach' | 'detach';

const getLicenseLabel = (type: ILicenseType): string => {
  if (type === LICENSE_TYPE.ALL) {
    return 'All license types';
  }
  return type;
};

const getPlatformLabel = (type: PlatformRoleType): string => {
  if (type === PLATFORM_ROLE_TYPE.PLATFORM_ADMIN) {
    return getI18nMessage(
      'userManagement',
      'PLATFORM_ROLE_LABELS.PLATFORM_ADMIN',
      'Platform Admin',
    );
  }
  if (type === PLATFORM_ROLE_TYPE.ORG_OWNER) {
    return getI18nMessage(
      'userManagement',
      'PLATFORM_ROLE_LABELS.ORG_OWNER',
      'Org Owner',
    );
  }
  if (type === PLATFORM_ROLE_TYPE.ORG_ADMIN) {
    return getI18nMessage(
      'userManagement',
      'PLATFORM_ROLE_LABELS.ORG_ADMIN',
      'Org Admin',
    );
  }
  if (type === PLATFORM_ROLE_TYPE.PLATFORM_AUDITOR) {
    return getI18nMessage(
      'userManagement',
      'PLATFORM_ROLE_LABELS.PLATFORM_AUDITOR',
      'Platform Auditor',
    );
  }
  if (type === PLATFORM_ROLE_TYPE.PLATFORM_SUPPORT) {
    return getI18nMessage(
      'userManagement',
      'PLATFORM_ROLE_LABELS.PLATFORM_SUPPORT',
      'Platform Support',
    );
  }
  return type;
};

const hasPlatformRole = (
  platformRole: PlatformRoleType | null,
  platformRoleNames: PlatformRoleType[],
) => platformRoleNames?.some(
  (roleName) => roleName === platformRole,
);

const getHoloxrRoleNames = (platformRoles: IPlatformRole[] = []): PlatformRoleType[] => (
  platformRoles
    .filter((role) => role.product === HOLOXR_PLATFORM_PRODUCT)
    .map((role) => role.roleName)
);

const buildRolesPatchPayload = (
  currentPlatformRoles: IPlatformRole[] = [],
  targetRole: PlatformRoleType,
  action: PlatformRolePatchAction,
  manageableRoles: PlatformRoleType[],
): IPlatformRole[] => {
  const currentManageableRoles = getHoloxrRoleNames(currentPlatformRoles)
    .filter((roleName) => roleName !== PLATFORM_ROLE_TYPE.USER
      && manageableRoles.includes(roleName));

  const nextManageableRoles = action === 'attach'
    ? [...new Set([...currentManageableRoles, targetRole])]
    : currentManageableRoles.filter((roleName) => roleName !== targetRole);

  return nextManageableRoles.map((roleName) => ({
    product: HOLOXR_PLATFORM_PRODUCT,
    roleName,
  }));
};

const isUserIndependent = (
  userUuid: string | undefined,
  organizationUuid: string | undefined,
) => userUuid === organizationUuid;

const isCurrentUserIndependent = () => isUserIndependent(
  getCurrentUuid(),
  getCurrentOrganizationUuid(),
);

export {
  getLicenseLabel,
  getPlatformLabel,
  hasPlatformRole,
  buildRolesPatchPayload,
  isUserIndependent,
  isCurrentUserIndependent,
};
export type { PlatformRolePatchAction };
